import { supabase } from "@/integrations/supabase/client";
import { PersonCategory, EntryType } from "@/types";

// People API
export const peopleAPI = {
  // Get all people
  async getPeople() {
    const { data, error } = await supabase
      .from('people')
      .select('*')
      .order('name');
    
    if (error) {
      
      throw error;
    }
    
    return data || [];
  },

  // Get people for a specific project
  async getPeopleByProject(projectId: string) {
    try {
      // First get all project_people records for this project
      const { data: projectPeople, error: ppError } = await supabase
        .from('project_people')
        .select('person_id, total_agreed, total_paid, currency')
        .eq('project_id', projectId);
      
      if (ppError) {
        
        // Return empty array instead of throwing
        return [];
      }
      
      if (!projectPeople || projectPeople.length === 0) {
        // No people assigned to this project
        return [];
      }
      
      // Extract person IDs
      const personIds = projectPeople.map(pp => pp.person_id);
      
      // Get person details for these IDs
      const { data: people, error: peopleError } = await supabase
        .from('people')
        .select('id, name, category, specialty, phone, email')
        .in('id', personIds);
      
      if (peopleError) {
        
        // Return empty array instead of throwing
        return [];
      }
      
      // Create a map of project_people records for easy lookup
      const projectPeopleMap = {};
      projectPeople.forEach(pp => {
        projectPeopleMap[pp.person_id] = pp;
      });
      
      // Format data to match the application's Person type
      return people.map(person => ({
        id: person.id,
        name: person.name,
        category: person.category,
        specialty: person.specialty,
        totalAgreed: projectPeopleMap[person.id]?.total_agreed || 0,
        totalPaid: projectPeopleMap[person.id]?.total_paid || 0,
        phone: person.phone,
        email: person.email
      }));
    } catch (error) {
      
      throw error;
    }
  },

  // Get people who have received payments from a project
  async getPeopleWithPaymentsByProject(projectId: string) {
    try {
      
      
      // First, get all entries for this project with a person_id
      // For expenses and given checks, these represent payments TO people
      
      const { data: entriesData, error: entriesError } = await supabase
        .from('entries')
        .select('id, person_id, type, original_amount, original_currency')
        .eq('project_id', projectId)
        .not('person_id', 'is', null);
      
      if (entriesError) {
        
        // Return empty array instead of throwing
        return [];
      }
      
      // Cast data to a more specific type to help with TypeScript
      const entries = entriesData || [];
      
      if (entries.length === 0) {
        
        return [];
      }
      
      
      
      // Get unique person IDs from entries
      const personIds = [...new Set(entries.map(entry => entry.person_id))];
      
      // Calculate total paid amount per person
      const personTotals: Record<string, number> = {};
      entries.forEach(entry => {
        if (!personTotals[entry.person_id]) {
          personTotals[entry.person_id] = 0;
        }
        
        // Add to totalPaid for various entry types that represent payments to people
        // Include expense, givenCheck, and any other payment types
        if (entry.type === 'expense' || 
            entry.type === 'givenCheck' || 
            entry.type === 'payment' || 
            entry.type === 'labor') {
          // Use original_amount for payment calculation
          const paymentAmount = entry.original_amount || 0;
          
          personTotals[entry.person_id] += paymentAmount;
        }
      });
      
      // Get person details
      const { data: peopleData, error: peopleError } = await supabase
        .from('people')
        .select('id, name, category, specialty, phone, email')
        .in('id', personIds);
      
      if (peopleError) {
        
        // Return empty array instead of throwing
        return [];
      }
      
      // Cast to proper type
      const people = peopleData || [];
      
      // Get project_people records to get the agreed amounts - use direct query
      const { data: projectPeopleData, error: projectPeopleError } = await supabase
        .from('project_people')
        .select('person_id, total_agreed')
        .eq('project_id', projectId);
      
      if (projectPeopleError) {
        
        // Continue without agreed amounts
      }
      
      // Cast to proper type
      const projectPeople = projectPeopleData || [];
      
      // Map agreed amounts by person ID
      const agreedAmounts: Record<string, number> = {};
      projectPeople.forEach(record => {
        agreedAmounts[record.person_id] = record.total_agreed || 0;
      });
      
      // Format the data to match our Person type
      const formattedPeople = people.map(person => {
        // Get the person's entries to determine their original currency
        const personEntries = entries.filter(entry => entry.person_id === person.id);
        // Use the most recent entry's currency, or default to TRY
        const originalCurrency = personEntries.length > 0 ? 
          personEntries[0].original_currency || 'TRY' : 'TRY';
          
        return {
          id: person.id,
          name: person.name,
          category: person.category,
          specialty: person.specialty,
          phone: person.phone,
          email: person.email,
          totalPaid: personTotals[person.id] || 0,
          totalAgreed: agreedAmounts[person.id] || 0,
          original_currency: originalCurrency
        };
      });
      
      
      
      return formattedPeople;
    } catch (error) {
      
      throw error;
    }
  },

  // Get a specific person by ID
  async getPerson(personId: string) {
    const { data, error } = await supabase
      .from('people')
      .select('*')
      .eq('id', personId)
      .single();
    
    if (error) {
      
      throw error;
    }
    
    return data;
  },

  // Create a new person
  async createPerson(personData: {
    name: string;
    category: string;
    specialty?: string;
    phone?: string;
    email?: string;
    country?: string;
    city?: string;
    created_by: string;
  }) {
    // Format data for the database
    const formattedData = {
      name: personData.name,
      category: personData.category,
      specialty: personData.specialty,
      phone: personData.phone,
      email: personData.email,
      country: personData.country,
      city: personData.city,
      user_id: personData.created_by
    };
    
    const { data, error } = await supabase
      .from('people')
      .insert(formattedData)
      .select()
      .single();
    
    if (error) {
      
      throw error;
    }
    
    return data;
  },

  // Add a person to a project
  async addPersonToProject(
    projectId: string,
    personId: string,
    totalAgreed?: number,
    totalPaid?: number,
    currency: string = 'TRY'
  ) {
    try {
      
      
      const formattedData = {
        project_id: projectId,
        person_id: personId,
        total_agreed: totalAgreed || 0,
        total_paid: totalPaid || 0,
        currency: currency
      };
      
      // First check if this person is already in the project
      const { data: existing, error: checkError } = await supabase
        .from('project_people')
        .select('id')
        .eq('project_id', projectId)
        .eq('person_id', personId);
      
      if (checkError) {
        
        throw checkError;
      }
      
      if (existing && existing.length > 0) {
        // Person already exists in this project, update the record
        const { data, error } = await supabase
          .from('project_people')
          .update({
            total_agreed: totalAgreed || 0,
            total_paid: totalPaid || 0,
            currency: currency
          })
          .eq('id', existing[0].id)
          .select();
        
        if (error) {
          
          throw error;
        }
        
        return data;
      } else {
        // Person does not exist in this project, insert new record
        const { data, error } = await supabase
          .from('project_people')
          .insert(formattedData)
          .select();
        
        if (error) {
          
          throw error;
        }
        
        return data;
      }
    } catch (error) {
      
      throw error;
    }
  },

  // Update a person
  async updatePerson(
    personId: string,
    personData: {
      name?: string;
      category?: string;
      specialty?: string;
      phone?: string;
      email?: string;
      country?: string;
      city?: string;
    }
  ) {
    const { data, error } = await supabase
      .from('people')
      .update(personData)
      .eq('id', personId)
      .select()
      .single();
    
    if (error) {
      
      throw error;
    }
    
    return data;
  },

  // Delete a person
  async deletePerson(personId: string) {
    const { error } = await supabase
      .from('people')
      .delete()
      .eq('id', personId);
    
    if (error) {
      
      throw error;
    }
    
    return true;
  },

  // Change any TL references to TRY
  async calculatePersonTotal(personId: string, projectId?: string, targetCurrency: string = 'TRY') {
    // ... existing code
  },

  // Link user by phone and fetch shared entries
async linkAndFetchSharedEntries(userId: string): Promise<any[]> {
  try {
    
    
    
    
    
    for (let i = 0; i < userId.length; i++) {
      
    }

    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      
      throw new Error("Geçersiz kullanıcı kimliği. QR okutma başarısız oldu.");
    }

    
    const startTime = Date.now();

    try {
      const { error: testQueryError } = await supabase
        .from('entries')
        .select('original_amount')
        .limit(1);

      if (testQueryError) {
        

        if (testQueryError.message.includes("column 'original_amount' does not exist")) {
          

          const { data: fallbackEntries, error: entriesError } = await supabase
            .from('entries')
            .select(`
              id, type, project_id, date, description, person_id, user_id,
              original_amount, original_currency, is_official, has_invoice
            `)
            .eq('person_id', userId);

          if (entriesError) {
            
            throw new Error("Paylaşılan kayıtlar alınırken hata oluştu.");
          }

          return this.processSharedEntries(fallbackEntries || [], startTime);
        }
      }
    } catch (testError) {
      
    }

    const { data: sampleEntries, error: sampleError } = await supabase
      .from('entries')
      .select('id, person_id')
      .limit(5);

    if (sampleError) {
      
    } else {
      
      if (sampleEntries && sampleEntries.length > 0) {
        const firstEntry = sampleEntries[0];
        
        
        
        
        
      }
    }

    const { data: entries, error: entriesError } = await supabase
      .from('entries')
      .select(`
        id, type, project_id, date, description, person_id, user_id,
        original_amount, original_currency, is_official, has_invoice
      `)
      .eq('person_id', userId);

    const queryTime = Date.now() - startTime;
    
    
    if (entries && entries.length > 0) {
      
      
    }

    if (entriesError) {
      
      throw new Error("Paylaşılan kayıtlar alınırken hata oluştu.");
    }

    return this.processSharedEntries(entries || [], startTime);
  } catch (error: any) {
    
    
    
    throw new Error(error.message || "Paylaşılan kayıtları getirirken bilinmeyen bir hata oluştu.");
  }
},

// Helper function to process shared entries using creator_lookup
async processSharedEntries(entries: any[], startTime: number): Promise<any[]> {
  

  if (entries.length === 0) {
    
    return [];
  }

  

  // First check if the creator_lookup table is accessible and has the expected row
  try {
    // Try a direct query with the exact project_id from the sample INSERT statement
    const { data: directLookup, error: directError } = await (supabase as any)
      .from('creator_lookup')
      .select('*')
      .eq('project_id', 'd0b21a20-d987-4918-8a9c-cd4d9fa188d1')
      .single();
    
    if (directError) {
      
      
    } else {
      
    }
  } catch (e) {
    
  }

  // Gather unique project_ids
  const projectIds = [...new Set(entries.map(e => e.project_id))];
  

  // Define creator type
  interface CreatorLookup {
    project_id: string;
    project_name: string;
    creator_id: string;
    creator_name: string;
  }

  // Fetch creator info for all projects in one go - use "any" to bypass type checking
  const { data: creators, error: creatorError } = await (supabase as any)
    .from('creator_lookup')
    .select('project_id, project_name, creator_id, creator_name')
    .in('project_id', projectIds);

  if (creatorError) {
    
  }

  // Log detailed info about creators result
  
  if (creators && creators.length > 0) {
    
  } else {
    
    
    // Additional debug query to check if the table exists and has data
    const { data: sampleLookup, error: sampleError } = await (supabase as any)
      .from('creator_lookup')
      .select('*')
      .limit(5);
    
    if (sampleError) {
      
      if (sampleError.message.includes("permission denied")) {
        
      } 
      if (sampleError.message.includes("does not exist")) {
        
      }
    } else {
      
    }
  }

  const creatorMap = new Map<string, CreatorLookup>(
    (creators || []).map((c: any) => [c.project_id, c as CreatorLookup])
  );

  // Log the mapping result
  
  if (creatorMap.size > 0) {
    for (const [projectId, creator] of creatorMap.entries()) {
      
    }
  }

  const processed = entries.map((entry: any) => {
    const creator = creatorMap.get(entry.project_id);
    

    return {
      ...entry,
      creatorId: creator?.creator_id || entry.user_id,
      creatorName: creator?.creator_name || 'Bilinmeyen Oluşturan',
      projectId: entry.project_id,
      projectName: creator?.project_name || 'Bilinmeyen Proje'
    };
  });

  const elapsed = Date.now() - startTime;
  

  return processed;
},


  // Helper function to get creator users and map entries
  async getCreatorUsersAndMapEntries(creatorIds: string[], entries: any[]): Promise<any[]> {
    
    
    try {
      const { data: creatorUsers, error: creatorError } = await supabase
        .from('users')
        .select('id, name')
        .in('id', creatorIds);

      if (creatorError) {
        
        // Continue without creator names - non-critical
      }

      

      const creatorMap = (creatorUsers || []).reduce((acc, curr) => {
        acc[curr.id] = curr.name;
        return acc;
      }, {} as Record<string, string>);

      
      const sharedEntries = entries.map((entry: any) => this.formatSharedEntry(entry, creatorMap));

      
      return sharedEntries;
    } catch (error) {
      
      // Fall back to basic mapping without creator names
      return entries.map((entry: any) => this.formatSharedEntry(entry, null));
    }
  },

  // Helper function to format a shared entry
  formatSharedEntry(entry: any, creatorMap: Record<string, string> | null): any {
    return {
      id: entry.id,
      type: entry.type,
      projectId: entry.project_id,
      date: entry.date,
      description: entry.description || '',
      original_currency: entry.original_currency || entry.currency || 'TRY',
      original_amount: entry.original_amount || entry.amount || 0,
      is_official: entry.is_official || false,
      hasInvoice: entry.has_invoice || false,
      personId: entry.person_id,
      creatorId: entry.user_id || 'unknown',
      creatorName: entry.user_id && creatorMap ? (creatorMap[entry.user_id] || 'Bilinmeyen Oluşturan') : 'Bilinmeyen Oluşturan',
      projectName: entry.project?.name || 'Bilinmeyen Proje',
    };
  },

  // Link a person with QR code
  async linkPersonWithQR(oldAnonId: string, scannedUserId: string): Promise<boolean> {
    try {
      // Validate inputs
      if (!oldAnonId || !scannedUserId) {
        
        throw new Error("Geçersiz giriş: Kişi veya QR kimliği eksik.");
      }
      
      // 1. Get the current person details (for later use and for user_id)
      const { data: currentPerson, error: personError } = await supabase
        .from('people')
        .select('*')
        .eq('id', oldAnonId)
        .single();
        
      if (personError) {
        
        throw new Error(`Mevcut kişi bulunamadı: ${personError.message}`);
      }
      
      // 2. Upsert a placeholder record with scannedUserId
      // If the record already exists, this does nothing
      const { error: upsertError } = await supabase
        .from('people')
        .upsert({ 
          id: scannedUserId,
          name: currentPerson.name,
          category: currentPerson.category,
          linked: true,
          user_id: currentPerson.user_id
        }, {
          onConflict: 'id'
        });
        
      if (upsertError) {
        
        throw new Error(`Hedef kişi oluşturulamadı: ${upsertError.message}`);
      }
      
      // 3. Update all entries related to this person
      const { error: entriesError } = await supabase
        .from('entries')
        .update({ person_id: scannedUserId })
        .eq('person_id', oldAnonId);
        
      if (entriesError) {
        
        throw new Error(`Kayıtlar güncellenemedi: ${entriesError.message}`);
      }
      
      // 4. Delete the old person record (cleanup)
      const { error: deleteError } = await supabase
        .from('people')
        .delete()
        .eq('id', oldAnonId);
      
      if (deleteError) {
        // Non-critical error - just log it but don't throw
        
      } else {
        
      }
      
      return true;
    } catch (error: any) {
      
      throw new Error(error.message || "Kişi ile QR bağlantısı kurulamadı.");
    }
  },

  // Unlink a person (remove QR connection)
  async unlinkPerson(realUserId: string): Promise<boolean> {
    
    
    try {
      // Validate input
      if (!realUserId) {
        
        throw new Error("Geçersiz giriş: Kişi kimliği eksik.");
      }
      
      // 1. Get the current person details to preserve data
      const { data: currentPerson, error: personError } = await supabase
        .from('people')
        .select('*')
        .eq('id', realUserId)
        .single();
        
      if (personError) {
        
        throw new Error(`Kişi bulunamadı: ${personError.message}`);
      }
      
      
      
      // 2. Generate a new standard UUID for the anonymous person
      // Import uuidv4 dynamically to avoid dependency issues
      const { v4: uuidv4 } = await import('uuid');
      const newAnonPersonId = uuidv4(); // Generate a standard UUID

      

      // 3. Insert a new row into people with the anonymous ID
      const { data: newPerson, error: insertError } = await supabase
        .from('people')
        .insert({
          id: newAnonPersonId, // Use the standard UUID
          name: currentPerson.name,
          category: currentPerson.category,
          specialty: currentPerson.specialty,
          avatar: currentPerson.avatar,
          email: currentPerson.email,
          phone: currentPerson.phone,
          user_id: currentPerson.user_id,
          linked: false
        })
        .select();
        
      if (insertError) {
        
        throw new Error(`Geçici kişi oluşturulamadı: ${insertError.message}`);
      }
      
      
      
      // 4. Update all entries related to this person
      const { data: entriesResult, error: entriesError } = await supabase
        .from('entries')
        .update({ person_id: newAnonPersonId }) // Update entries with the new standard UUID
        .eq('person_id', realUserId);
        
      if (entriesError) {
        
        throw new Error(`Kayıtlar güncellenemedi: ${entriesError.message}`);
      }
      
      
      
      // 5. Delete the original person record
      const { error: deleteError } = await supabase
        .from('people')
        .delete()
        .eq('id', realUserId);
      
      if (deleteError) {
        // Non-critical error - just log it but don't throw
        
        
      } else {
        
      }
      
      
      return true;
    } catch (error: any) {
      
      
      // Show an alert and log the error
      throw new Error(error.message || "Kişi bağlantısı kaldırılamadı.");
    }
  }
};
